import * as React from "react";

export interface ServiceAreaItem {
  name: string;
  region: string;
  url?: string;
}

export interface ServiceAreaProps {
  serviceType: string;
  providerId?: string;
  description?: string;
  url?: string;
  areas: ServiceAreaItem[];
  title?: string;
}

/** Pure JSON-LD builder — exported so tests/snapshots can validate it. */
export function buildServiceAreaLd(props: Omit<ServiceAreaProps, "title">) {
  const {
    serviceType,
    providerId = "https://lehitradessaeo.com/#organization",
    description,
    url,
    areas,
  } = props;
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType,
    ...(description ? { description } : {}),
    ...(url ? { url } : {}),
    provider: { "@id": providerId },
    areaServed: areas.map((a) => ({
      "@type": "City",
      name: `${a.name}, ${a.region}`,
    })),
  } as const;
}

/**
 * Renders a list of service areas AND emits Schema.org Service JSON-LD with
 * `areaServed` populated automatically. Drop-in component — schema stays in
 * sync with whatever cities you render.
 */
export function ServiceArea({
  serviceType,
  providerId = "https://lehitradessaeo.com/#organization",
  description,
  url,
  areas,
  title,
}: ServiceAreaProps) {
  const ld = buildServiceAreaLd({ serviceType, providerId, description, url, areas });

  return (
    <section aria-label={title ?? `${serviceType} service areas`} className="mt-6">
      {title && (
        <h3 className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">
          {title}
        </h3>
      )}
      <ul className="mt-3 flex flex-wrap gap-2 text-sm">
        {areas.map((a) => {
          const label = `${a.name}, ${a.region}`;
          return (
            <li key={label}>
              {a.url ? (
                <a
                  href={a.url}
                  className="inline-flex rounded-full border border-border px-3 py-1 text-muted-foreground hover:border-primary/40 hover:text-foreground"
                >
                  {label}
                </a>
              ) : (
                <span className="inline-flex rounded-full border border-border px-3 py-1 text-muted-foreground">
                  {label}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }}
      />
    </section>
  );
}